import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { RmsTenantAuthResponse, RmsTenantProfileResponse } from '../models/models';

// Distinct localStorage keys from reminders' `reminders.accessToken`/`reminders.refreshToken`
// (and the landlord site session) so a tenant's phone-OTP session can never collide with a
// landlord's shared-site session, even in the same browser.
const ACCESS_TOKEN_KEY = 'rms.tenant.accessToken';
const REFRESH_TOKEN_KEY = 'rms.tenant.refreshToken';

/**
 * Tenants have no site account — they authenticate purely via WhatsApp/phone OTP against
 * `/api/rms/tenant-auth/*`. This is a self-contained auth service, deliberately NOT reusing
 * `auth/core/auth.service.ts` (that one is for the shared landlord/site account).
 */
@Injectable({ providedIn: 'root' })
export class TenantAuthService {
  private readonly http = inject(HttpClient);

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly accessTokenSignal = signal<string | null>(
    this.isBrowser ? localStorage.getItem(ACCESS_TOKEN_KEY) : null,
  );
  private readonly currentTenantSignal = signal<RmsTenantProfileResponse | null>(null);

  readonly accessToken = this.accessTokenSignal.asReadonly();
  readonly currentTenant = this.currentTenantSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.accessTokenSignal());

  get refreshToken(): string | null {
    return this.isBrowser ? localStorage.getItem(REFRESH_TOKEN_KEY) : null;
  }

  sendOtp(phone: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/rms/tenant-auth/send-otp', { phone });
  }

  verifyOtp(phone: string, otp: string): Observable<RmsTenantAuthResponse> {
    return this.http
      .post<RmsTenantAuthResponse>('/api/rms/tenant-auth/verify-otp', { phone, otp })
      .pipe(tap((res) => this.storeSession(res)));
  }

  refresh(): Observable<RmsTenantAuthResponse> {
    return this.http
      .post<RmsTenantAuthResponse>('/api/rms/tenant-auth/refresh', { refreshToken: this.refreshToken })
      .pipe(tap((res) => this.storeSession(res)));
  }

  logout(): void {
    const refreshToken = this.refreshToken;
    this.clearSession();
    if (refreshToken) {
      this.http.post('/api/rms/tenant-auth/logout', { refreshToken }).subscribe({ error: () => {} });
    }
  }

  private storeSession(res: RmsTenantAuthResponse): void {
    if (this.isBrowser) {
      localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
    }
    this.accessTokenSignal.set(res.accessToken);
    this.currentTenantSignal.set(res.tenant);
  }

  private clearSession(): void {
    if (this.isBrowser) {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    }
    this.accessTokenSignal.set(null);
    this.currentTenantSignal.set(null);
  }
}
